import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { requestOtp, verifyOtp } from '../../../shared/api/auth.service';
import { RpcError } from '../../../shared/api/rpc-client';
import {
  clearReferralAttributionId,
  readReferralAttributionId,
} from '../../../shared/auth';
import { resolveApiLocale, resolveAppLanguage } from '../../../shared/config';
import { useClipboard } from '../../../shared/hooks/use-clipboard';
import { useEmployeeSession } from '../../../shared/hooks/use-employee-session';

import { resolveVerifyPhonePreview } from './auth-contact';

const VERIFY_CODE_LENGTH = 6;
const VERIFY_RESEND_INITIAL_SECONDS = 20;

export type VerifyCodeState = 'idle' | 'success' | 'error';

type VerifyForm = {
  code: string;
  codeError: string | null;
  codeSlots: ReadonlyArray<string>;
  codeState: VerifyCodeState;
  isResendAvailable: boolean;
  isVerifying: boolean;
  pasteCodeFromClipboard: () => Promise<void>;
  phonePreview: string;
  resendCode: () => Promise<void>;
  resendCountdown: string;
  setCode: (value: string) => void;
  shouldRedirectToLogin: boolean;
};

type UseVerifyFormOptions = {
  initialRetryAfter?: number;
  initialExpiresIn?: number;
};

function resolveFirstNewDigit(currentCode: string, nextDigits: string) {
  const maxSharedLength = Math.min(currentCode.length, nextDigits.length);
  let firstDifferentIndex = 0;

  while (
    firstDifferentIndex < maxSharedLength &&
    currentCode[firstDifferentIndex] === nextDigits[firstDifferentIndex]
  ) {
    firstDifferentIndex += 1;
  }

  return nextDigits[firstDifferentIndex] ?? '';
}

function resolveSmartResetCode(
  currentCode: string,
  nextValue: string,
  currentCodeState: VerifyCodeState,
) {
  const nextDigits = nextValue.replace(/\D/g, '');
  const nextCode = nextDigits.slice(0, VERIFY_CODE_LENGTH);

  if (currentCodeState !== 'error') {
    return nextCode;
  }

  if (nextDigits === currentCode || nextDigits.length < currentCode.length) {
    return nextCode;
  }

  return resolveFirstNewDigit(currentCode, nextDigits);
}

function formatCountdown(seconds: number) {
  return `00:${String(Math.max(0, seconds)).padStart(2, '0')}`;
}

function resolveVerifyError(
  error: unknown,
  t: (key: string) => string,
): string {
  if (error instanceof RpcError) {
    const msg = error.message.toLowerCase();
    if (
      msg.includes('too many requests') ||
      msg.includes('temporarily locked')
    ) {
      return t('verify.tooManyRequests');
    }
    if (
      msg.includes('otp code expired') ||
      msg.includes('no otp code requested')
    ) {
      return t('verify.expiredCode');
    }
  }
  return t('verify.invalidCode');
}

export function useVerifyForm({
  initialRetryAfter,
  initialExpiresIn,
}: UseVerifyFormOptions = {}): VerifyForm {
  const { t, i18n } = useTranslation();
  const { authContact, setSessionToken } = useEmployeeSession();
  const clipboard = useClipboard();
  const phonePreview = useMemo(
    () => resolveVerifyPhonePreview(authContact),
    [authContact],
  );
  const [code, setCodeInternal] = useState('');
  const [codeState, setCodeState] = useState<VerifyCodeState>('idle');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(
    initialRetryAfter ?? VERIFY_RESEND_INITIAL_SECONDS,
  );
  const [expiresInSeconds, setExpiresInSeconds] = useState<number | null>(
    initialExpiresIn ?? null,
  );
  const verifyOtpMutation = useMutation({
    mutationFn: ({
      code,
      locale,
      phone,
      referrerId,
    }: {
      code: string;
      locale: string;
      phone: string;
      referrerId: number;
    }) => verifyOtp(phone, code, locale, referrerId),
  });
  const resendOtpMutation = useMutation({
    mutationFn: ({ locale, phone }: { locale: string; phone: string }) =>
      requestOtp(phone, locale),
  });

  useEffect(() => {
    if (remainingSeconds === 0) {
      return;
    }

    const timerId = window.setTimeout(() => {
      setRemainingSeconds(current => Math.max(0, current - 1));
    }, 1000);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [remainingSeconds]);

  useEffect(() => {
    if (expiresInSeconds === null || expiresInSeconds === 0) {
      return;
    }

    const timerId = window.setTimeout(() => {
      setExpiresInSeconds(current =>
        current !== null ? Math.max(0, current - 1) : null,
      );
    }, 1000);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [expiresInSeconds]);

  useEffect(() => {
    if (expiresInSeconds !== 0) {
      return;
    }

    setExpiresInSeconds(null);
    setCodeState(prev => (prev === 'success' ? prev : 'error'));
    setCodeError(t('verify.expiredCode'));
  }, [expiresInSeconds, t]);

  const triggerVerify = async (nextCode: string) => {
    if (!authContact || verifyOtpMutation.isPending) {
      return;
    }

    const phone = `${authContact.countryCode}${authContact.phoneDigits}`;
    const locale = resolveApiLocale(resolveAppLanguage(i18n.language));
    const referrerId = readReferralAttributionId() ?? 0;

    try {
      const result = await verifyOtpMutation.mutateAsync({
        code: nextCode,
        locale,
        phone,
        referrerId,
      });

      clearReferralAttributionId();
      setSessionToken(result.token);
      setCodeState('success');
      setCodeError(null);
    } catch (error) {
      setCodeState('error');
      setCodeError(
        error instanceof RpcError && error.message === 'Network request failed'
          ? t('login.networkError')
          : resolveVerifyError(error, t),
      );
    }
  };

  const setCode = (value: string) => {
    const nextCode = resolveSmartResetCode(code, value, codeState);

    setCodeInternal(nextCode);

    if (nextCode.length < VERIFY_CODE_LENGTH) {
      if (codeState !== 'idle') {
        setCodeState('idle');
      }
      if (codeError !== null) {
        setCodeError(null);
      }
      return;
    }

    if (codeState !== 'idle' || !authContact) {
      return;
    }

    void triggerVerify(nextCode);
  };

  const pasteCodeFromClipboard = async () => {
    const clipboardContent = await clipboard.readText();
    if (clipboardContent !== null) {
      setCode(clipboardContent);
    }
  };

  const resendCode = async () => {
    if (!authContact) {
      return;
    }

    const phone = `${authContact.countryCode}${authContact.phoneDigits}`;
    const locale = resolveApiLocale(resolveAppLanguage(i18n.language));

    try {
      const result = await resendOtpMutation.mutateAsync({
        locale,
        phone,
      });

      setRemainingSeconds(result.retryAfter);
      setExpiresInSeconds(result.expiresIn);
      setCodeInternal('');
      setCodeState('idle');
      setCodeError(null);
    } catch {
      setRemainingSeconds(VERIFY_RESEND_INITIAL_SECONDS);
    }
  };

  const codeSlots = useMemo(
    () =>
      Array.from(
        { length: VERIFY_CODE_LENGTH },
        (_, index) => code[index] ?? '',
      ),
    [code],
  );
  const resendCountdown = useMemo(
    () => formatCountdown(remainingSeconds),
    [remainingSeconds],
  );

  return {
    code,
    codeError,
    codeSlots,
    codeState,
    isResendAvailable: remainingSeconds === 0,
    isVerifying: verifyOtpMutation.isPending,
    pasteCodeFromClipboard,
    phonePreview: phonePreview ?? '',
    resendCode,
    resendCountdown,
    setCode,
    shouldRedirectToLogin: phonePreview === null,
  };
}
